"use client";

import React, { useEffect, useState, ElementType } from "react";
import DOMPurify from "dompurify";

interface CustomSVGProps extends React.HTMLAttributes<HTMLElement> {
  src: string;
  wrapper?: ElementType;
  width?: number | string;
  height?: number | string;
  className?: string;
  "aria-hidden"?: boolean | "true" | "false";
  role?: string;
  alt?: string;
  "aria-label"?: string;
  fallback?: React.ReactNode;
}

export const CustomSVG: React.FC<CustomSVGProps> = ({
  src,
  wrapper: Wrapper = "span",
  width,
  height,
  className = "",
  "aria-hidden": ariaHidden,
  role,
  alt,
  "aria-label": ariaLabel,
  fallback = null,
  ...rest
}) => {
  const [svgContent, setSvgContent] = useState<string | null>(null);
  const [error, setError] = useState<boolean>(false);

  useEffect(() => {
    let isMounted = true;

    if (!src) {
      setSvgContent(null);
      return;
    }

    const fetchSVG = async () => {
      try {
        const response = await fetch(src);
        if (!response.ok) {
          throw new Error(`Failed to fetch SVG: ${response.statusText}`);
        }
        const text = await response.text();
        if (isMounted) {
          setSvgContent(text);
          setError(false);
        }
      } catch (err) {
        console.error("Error fetching SVG:", err);
        if (isMounted) {
          setError(true);
        }
      }
    };

    fetchSVG();

    return () => {
      isMounted = false;
    };
  }, [src]);

  if (error || !svgContent) {
    return <>{fallback}</>;
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(svgContent, "image/svg+xml");
  const svgElement = doc.querySelector("svg");

  if (!svgElement) {
    return <>{fallback}</>;
  }

  // Apply props to the SVG element
  if (width) svgElement.setAttribute("width", String(width));
  if (height) svgElement.setAttribute("height", String(height));
  if (ariaHidden !== undefined)
    svgElement.setAttribute("aria-hidden", String(ariaHidden));
  if (role) svgElement.setAttribute("role", role);
  if (alt) svgElement.setAttribute("alt", alt);
  if (ariaLabel) svgElement.setAttribute("aria-label", ariaLabel);

  const serializedSVG = new XMLSerializer().serializeToString(svgElement);
  const sanitizedSVG = DOMPurify.sanitize(serializedSVG);

  return (
    <Wrapper
      className={className}
      dangerouslySetInnerHTML={{ __html: sanitizedSVG }}
      {...rest}
    />
  );
};
